import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import { CanonicalWidgetDataSchema } from '@trinity/chat/types/dashboard-data';
import type { WidgetDataProxy, WidgetDataRequest } from '@trinity/chat/types/widget-data-proxy';
import { collectAllWidgets } from '@trinity/chat/utils/dashboard-widgets';
import type {
  DashboardAgentOption,
  DashboardConnectionOption,
  DashboardPreviewScopeOption,
} from '@trinity/chat-components/containers/Dashboard/context';
import { DashboardDataContext, useDashboardConfig } from '@trinity/chat-components/containers/Dashboard/context';
import type { DataProvider } from './DataProvider';

/** 5-minute polling interval for agent-sourced widgets in live mode */
const LIVE_POLL_INTERVAL_MS = 5 * 60 * 1000;

type Props = {
  children: React.ReactNode;
  /** The DataProvider to use for fetching widget data */
  provider: DataProvider;
  /**
   * `builder` — fetch once on mount; no polling (data previewed in the editor).
   * `view`    — fetch once on mount from snapshots; no polling (management app viewer).
   * `live`    — fetch on mount then poll every 5 minutes (agent widgets refresh).
   * Default: `live`.
   */
  mode?: 'builder' | 'view' | 'live';
  /**
   * Builder-only: used by the "Fetch Preview" button to proxy a request to a remote
   * data source and inject the result as a live preview.
   */
  proxyFetch?: WidgetDataProxy;
  /** Optional brand identifier — forwarded to proxyFetch so remote requests include brand context. */
  brandIdentifier?: string;
  /**
   * When true, the page's `dataSettings.previewScope` (if set) replaces `brandIdentifier` and names its
   * application for every fetch. For the management builder and its preview; renderers leave it off and ignore the setting.
   */
  usePreviewScope?: boolean;
  /**
   * Builder-only: list of available HTTP agents that the user can select from
   * when configuring an agent data source on a widget.
   */
  agents?: DashboardAgentOption[];
  /** Builder-only: data connections to select from when configuring a connection data source. */
  connections?: DashboardConnectionOption[];
  /** Builder-only: the brand and application pairs the page's preview scope can be chosen from. */
  previewScopes?: DashboardPreviewScopeOption[];
};

const EMPTY_AGENTS: DashboardAgentOption[] = [];
const EMPTY_CONNECTIONS: DashboardConnectionOption[] = [];
const EMPTY_PREVIEW_SCOPES: DashboardPreviewScopeOption[] = [];

/**
 * DashboardDataContainer — connects a DataProvider to DashboardDataContext.
 *
 * Each host app wraps the dashboard with its appropriate DataProvider:
 * - Management app (builder): `ConfigAwareDataProvider`, mode="builder"
 * - Chat app (renderer): `ConfigAwareDataProvider`, mode="live"
 * - Sentiment frontend: `ConfigAwareDataProvider`, mode="live"
 */
export function DashboardDataContainer({
  children,
  provider,
  mode = 'live',
  proxyFetch,
  brandIdentifier,
  usePreviewScope = false,
  agents = EMPTY_AGENTS,
  connections: connectionsProp,
  previewScopes = EMPTY_PREVIEW_SCOPES,
}: Props): React.ReactElement {
  const { config } = useDashboardConfig();
  const connections = connectionsProp ?? EMPTY_CONNECTIONS;
  const hasConnections = connectionsProp !== undefined;
  // The brand and application every fetch uses; a ref so the fetch callbacks stay stable while they change.
  const previewScope = usePreviewScope ? config.dataSettings?.previewScope : undefined;
  const effectiveBrandIdentifier = previewScope?.brandIdentifier ?? brandIdentifier;
  const effectiveApplicationIdentifier = previewScope?.applicationIdentifier;
  const scopeRef = useRef({ brandIdentifier: effectiveBrandIdentifier, applicationIdentifier: effectiveApplicationIdentifier });
  scopeRef.current = { brandIdentifier: effectiveBrandIdentifier, applicationIdentifier: effectiveApplicationIdentifier };
  const [widgetData, setWidgetData] = useState<Map<string, CanonicalWidgetData>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  // Keep a stable ref to the provider so the useEffect can always access the latest one
  const providerRef = useRef(provider);
  providerRef.current = provider;

  // Extract all widgets from the nested block structure
  const allWidgets = useMemo(() => collectAllWidgets(config), [config]);
  // Stable ref so polling callbacks don't re-fire on every config change
  const allWidgetsRef = useRef(allWidgets);
  allWidgetsRef.current = allWidgets;

  // Monotonic counter so only the latest bulk fetch updates data and loading state: a change of preview scope
  // starts a new fetch while the old one may still be in flight, and it must not overwrite the newer result.
  const fetchAllSeqRef = useRef(0);

  const fetchAll = useCallback(async () => {
    const seq = ++fetchAllSeqRef.current;
    setIsLoading(true);
    try {
      const data = await providerRef.current.fetchAllWidgetData(allWidgetsRef.current, scopeRef.current);
      if (seq === fetchAllSeqRef.current) setWidgetData(data);
    } finally {
      if (seq === fetchAllSeqRef.current) setIsLoading(false);
    }
  }, []);

  const refreshWidget = useCallback(async (widgetId: string) => {
    const widget = allWidgetsRef.current.find(w => w.id === widgetId);
    if (!widget) return;
    const data = await providerRef.current.fetchWidgetData(widgetId, widget, scopeRef.current);
    if (data !== null) {
      setWidgetData(prev => {
        const next = new Map(prev);
        next.set(widgetId, data);
        return next;
      });
    }
  }, []);

  // Fetch all data on mount and whenever widget configurations change.
  // allWidgets recomputes (new reference) whenever config changes, so this effect
  // re-fires on every Apply in the builder — giving the user an immediate preview.
  // Debounced after first render to avoid re-fetching remote-source widgets on
  // every keystroke in the builder. A new scope refetches too, since it changes what remote sources return.
  const isFirstFetch = useRef(true);
  useEffect(() => {
    if (isFirstFetch.current) {
      isFirstFetch.current = false;
      fetchAll().catch(() => undefined);
      return;
    }
    const timeout = setTimeout(() => { fetchAll().catch(() => undefined); }, 300);
    return () => clearTimeout(timeout);
  }, [fetchAll, allWidgets, effectiveBrandIdentifier, effectiveApplicationIdentifier]);

  // Live mode only: poll agent-sourced widgets every 5 minutes
  useEffect(() => {
    if (mode !== 'live') return;
    const id = setInterval(() => { fetchAll().catch(() => undefined); }, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mode, fetchAll]);

  // proxyFetch ref so the callback is always up-to-date without re-creating fetchSourcePreview
  const proxyFetchRef = useRef(proxyFetch);
  proxyFetchRef.current = proxyFetch;

  const updateWidgetData = useCallback((widgetId: string, data: CanonicalWidgetData | null) => {
    setWidgetData(prev => {
      const next = new Map(prev);
      if (data === null) {
        next.delete(widgetId);
      } else {
        next.set(widgetId, data);
      }
      return next;
    });
  }, []);

  const fetchSourcePreview = useCallback(async (request: WidgetDataRequest, widgetId: string): Promise<CanonicalWidgetData | null> => {
    if (!proxyFetchRef.current) return null;
    try {
      const raw = await proxyFetchRef.current({ ...request, ...scopeRef.current });
      if (raw !== null && raw !== undefined) {
        const parsed = CanonicalWidgetDataSchema.safeParse(raw);
        if (!parsed.success) return null;
        // Safe cast: safeParse validated the shape; hand-written CanonicalWidgetData
        // mirrors the schema with stricter required fields.
        const data = parsed.data as CanonicalWidgetData;
        updateWidgetData(widgetId, data);
        return data;
      }
      return null;
    } catch {
      return null;
    }
  }, [updateWidgetData]);

  const value = useMemo(() => ({
    widgetData,
    isLoading,
    refreshWidget,
    refreshAll: fetchAll,
    fetchSourcePreview,
    updateWidgetData,
    agents,
    connections,
    hasConnections,
    previewScopes,
  }), [widgetData, isLoading, refreshWidget, fetchAll, fetchSourcePreview, updateWidgetData, agents, connections, hasConnections, previewScopes]);

  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}
